import React, { useState, useRef, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Row,
  Col,
  Card,
  Divider,
  Table as AntdTable,
  Modal,
  Radio,
  RadioChangeEvent,
  RadioGroupProps,
  message,
} from "antd";
import { CardContainer } from "../../components/Card/CardContainer";
import { useEffectOnce } from "react-use";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import styled from "styled-components";
import Text from "../../components/Text/Text";
// import type { DragEndEvent } from "@dnd-kit/core";
import { type DragEndEvent, DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ColumnsType } from "antd/es/table";
import { MenuOutlined, MinusCircleOutlined, PushpinFilled } from "@ant-design/icons";
import color from "../../resource/color";
import Input from "../../components/Input/Input";
import { FlexRow } from "../../components/Container/Container";
import AutoComplete from "../../components/AutoComplete/AutoComplete";
import { getNewsList, getPinedNewsList, updatePinedNewsList } from "../../datasource/News";
import { NewsEntity, PinedNewsEntity } from "../../entities/NewsEntity";
import { PinPage } from "../../definitions/news";
import { ApplicationName } from "../../definitions/applicationName";
import image from "../../resource/image";

const boxHeight = 57;

const IndexBox = styled.div`
  width: 56px;
  height: ${boxHeight}px;
  margin-top: 12px;

  border-radius: 4px;
  background: #6b7995;

  color: #f3f6f9;
  font-family: Helvetica;
  font-size: 24px;
  font-weight: 700;

  display: flex;
  align-items: center;
  justify-content: center;
`;

const AddBox = styled.div`
  width: 100%;
  height: ${boxHeight}px;

  border-radius: 4px;
  border: 1px dashed ${color.primary};
  background: white;

  color: ${color.primary};
  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
`;

const EmptyBox = styled.div`
  width: 100%;
  height: ${boxHeight}px;
  margin-top: 12px;

  border-radius: 4px;
  background: rgba(239, 242, 249, 0.4);

  color: ${color.Text3};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Table = styled(AntdTable)`
  .ant-table table {
    border-spacing: 0 12px;
  }
  .ant-table-cell {
    background: ${color.background1} !important;
    padding: 8px 16px !important;

    :last-child {
      border-radius: 0 4px 4px 0;
    }
    :first-child {
      border-radius: 4px 0 0 4px;
    }
  }
`;

const TableRow = ({ children, ...props }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {React.Children.map(children, (child) => {
        if ((child as any).key === "sort") {
          return React.cloneElement(child as any, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{ touchAction: "none", cursor: "move" }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};

const TemplateArray = Array.from({ length: 5 }, (v, i) => i + 1);

export const PinedNewsPage: React.FC = (props: any) => {
  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;

  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { company, firstname, lastname } = userProfile;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [app, setApp] = useState<ApplicationName>("SALE");
  const [mainPageList, setMainPageList] = useState<PinedNewsEntity[]>([]);
  const [newsPageList, setNewsPageList] = useState<PinedNewsEntity[]>([]);
  const [deletedList, setDeletedList] = useState<PinedNewsEntity[]>([]);
  const [allNewsList, setAllNewsList] = useState<NewsEntity[]>([]);
  const [uploading, setUploading] = useState(false);

  useEffectOnce(() => {
    fetchData();
  });

  const fetchData = async (app?: ApplicationName) => {
    const formatData = (list: PinedNewsEntity[]) =>
      list
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((n, i) => {
          const news: PinedNewsEntity = {
            ...n,
            key: `${i + 1}`,
          };
          return news;
        });
    try {
      setLoading(true);
      await getPinedNewsList({ company, app: app || "SALE" }).then(({ responseData }) => {
        console.log("getPinedNewsList", responseData);
        const mainPageList = responseData.filter((n: PinedNewsEntity) => n.page === "MAIN_PAGE");
        const newsPageList = responseData.filter((n: PinedNewsEntity) => n.page === "NEWS_PAGE");

        setMainPageList(formatData(mainPageList));
        setNewsPageList(formatData(newsPageList));
      });

      await getNewsList({
        company,
        status: "PUBLISHED",
        take: 999,
        page: 1,
        application: app || "SALE",
      }).then(({ responseData }) => {
        setAllNewsList(responseData?.data);
      });
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const validateInput = () => {
    return mainPageList.every((e) => e.newsId) && newsPageList.every((e) => e.newsId);
  };

  const onUpdate = async () => {
    const formatData = (list: PinedNewsEntity[], page: PinPage) =>
      list.map((n, i) => {
        return {
          ...n,
          order: i + 1,
          page,
          company,
          app,
        };
      });
    setUploading(true);
    await updatePinedNewsList({
      newsList: [
        ...formatData(mainPageList, "MAIN_PAGE"),
        ...formatData(newsPageList, "NEWS_PAGE"),
        ...deletedList.map((n) => ({ pinedNewsId: n.pinedNewsId, isDelete: true, app })),
      ],
      createBy: firstname + " " + lastname,
    })
      .then((res) => {
        console.log(res);
        setUploading(false);
        navigate(0);
      })
      .catch((err) => {
        console.log(err);
        setUploading(false);
        message.error("ไม่สามารถบันทึกปักหมุดข่าวสารได้");
      });
  };

  const PageTitle = () => {
    return <PageTitleNested title={"รายการปักหมุดข่าวสาร"} showBack={false} />;
  };

  const Group = ({
    title,
    dataSource,
    setDataSource,
    groupId,
  }: {
    title: string;
    dataSource: PinedNewsEntity[];
    setDataSource: (any: any) => void;
    groupId: string;
  }) => {
    const columns: ColumnsType<any> = [
      {
        key: "sort",
        width: 50,
      },
      {
        title: "ข่าวสารที่เลือก",
        dataIndex: "topic",
        render: (value, record, index) => {
          return (
            <FlexRow align='center' justify='space-between'>
              <PushpinFilled style={{ color: "#F2C94C", fontSize: 20 }} />

              <AutoComplete
                defaultValue={value}
                style={{ width: "calc(100% - 32px)" }}
                options={allNewsList
                  .filter((e) => !dataSource.find((s) => s.topic === e.topic))
                  .map((e) => ({ value: e.newsId, label: e.topic }))}
                onSelect={(val, option) => {
                  const newData = [...dataSource];
                  newData[index] = {
                    ...dataSource[index],
                    topic: option.label,
                    newsId: val,
                  };
                  setDataSource(newData);
                }}
              />
            </FlexRow>
          );
        },
      },
      {
        title: "",
        width: 50,
        render: (value, record, index) => {
          return (
            <Row justify='end'>
              <MinusCircleOutlined
                style={{ color: color.error }}
                onClick={() => {
                  Modal.confirm({
                    title: "ต้องการยืนยันการลบหมุดข่าวสาร",
                    content:
                      "โปรดตรวจสอบหมุดข่าวสารที่คุณต้องการลบ ก่อนกดยืนยัน เพราะอาจส่งผลต่อการทำงานของผู้ดูแลระบบ",
                    onOk: () => {
                      setDataSource(dataSource.filter((v: any, i: number) => i !== index));
                      if (record.pinedNewsId) setDeletedList([...deletedList, record]);
                    },
                  });
                }}
              />
            </Row>
          );
        },
      },
    ];

    const onAdd = () => {
      setDataSource([...dataSource, { key: `${dataSource.length + 1}` }]);
    };

    const onDragEnd = ({ active, over }: DragEndEvent) => {
      if (active.id !== over?.id) {
        setDataSource((previous: any) => {
          const activeIndex = previous.findIndex((i: any) => i.key === active.id);
          const overIndex = previous.findIndex((i: any) => i.key === over?.id);
          return arrayMove(previous, activeIndex, overIndex);
        });
      }
    };

    return (
      <>
        <br />
        <br />
        <Text level={5} fontWeight={700}>
          {title}
        </Text>
        <br />
        <br />
        <Row gutter={16}>
          <Col style={{ width: 72 }}>
            <Row justify='center'>
              <Text level={6} fontWeight={700} style={{ color: "#6B7995" }}>
                ลำดับ
              </Text>
            </Row>
            {TemplateArray.map((i) => {
              return <IndexBox key={i}>{i}</IndexBox>;
            })}
          </Col>
          <Col style={{ width: "calc(100% - 72px)" }}>
            <Row style={{ padding: "0px 10px" }}>
              <Text level={6} fontWeight={700} style={{ color: "#6B7995" }}>
                ข่าวสารที่เลือก
              </Text>
            </Row>
            <div
              style={
                dataSource.length <= 0
                  ? { height: 12, overflow: "hidden", visibility: "hidden" }
                  : {}
              }
            >
              <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                <SortableContext
                  // rowKey array
                  items={dataSource.map((i: any) => i.key)}
                  strategy={verticalListSortingStrategy}
                >
                  <Table
                    components={{
                      body: {
                        row: TableRow,
                      },
                    }}
                    rowKey='key'
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    showHeader={false}
                  />
                </SortableContext>
              </DndContext>
            </div>
            {TemplateArray.map((v, i) => {
              return dataSource[i] ? (
                <></>
              ) : i === dataSource.length ? (
                <AddBox onClick={onAdd}>+ เพิ่มปักหมุดข่าวสาร</AddBox>
              ) : (
                <EmptyBox>ไม่มีปักหมุดข่าวสาร</EmptyBox>
              );
            })}
          </Col>
        </Row>
      </>
    );
  };

  const appRadioProps: RadioGroupProps = {
    optionType: "button",
    onChange: ({ target: { value } }: RadioChangeEvent) => {
      setApp(value);
      fetchData(value);
    },
    options: [
      {
        label: (
          <span>
            <img src={image.iconSaleApp} width={24} height={24} />
            &nbsp;Sale App
          </span>
        ),
        value: "SALE",
      },
      {
        label: (
          <span>
            <img src={image.iconShopApp} width={24} height={24} />
            &nbsp;Shop App
          </span>
        ),
        value: "SHOP",
      },
    ],
    value: app,
    defaultValue: "SALE",
    size: "large",
    style: { padding: "15px 15px" },
  };

  return loading ? (
    <div className='container '>
      <Card loading />
    </div>
  ) : (
    <Row justify={"space-between"} gutter={8}>
      <Col span={24}>
        <CardContainer>
          <PageTitle />
          <br />
          <Row>
            <Radio.Group {...appRadioProps} />
          </Row>
          <Group
            title={"หน้าหลักในแอปพลิเคชัน"}
            dataSource={mainPageList}
            setDataSource={setMainPageList}
            groupId={"main"}
          />
          <Group
            title={"หน้าข่าวสารทั้งหมดในแอปพลิเคชัน"}
            dataSource={newsPageList}
            setDataSource={setNewsPageList}
            groupId={"news"}
          />
          <Divider />
          <Row align='middle' justify='end'>
            <Button
              type='primary'
              htmlType='submit'
              size='large'
              style={{ width: 136 }}
              loading={uploading}
              disabled={!validateInput()}
              onClick={() => {
                Modal.confirm({
                  title: "บันทึกการเปลี่ยนแปลง",
                  onOk: onUpdate,
                });
              }}
            >
              บันทึก
            </Button>
          </Row>
        </CardContainer>
      </Col>
    </Row>
  );
};
